import React, { useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Form, Button, Row, Col, Card, Alert } from 'react-bootstrap';
import { PageHeader, Loading } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './AddVoucherThemes.module.scss';
import { toast } from 'react-toastify';
import { AddVoucherThemesApi, getVoucherThemeCategories } from '../../api/voucherTheme';
import {
  INITIAL_STATE,
  SUCCESS_MESSAGE,
  TOAST_MESSAGES,
  VOUCHER_THEME_FORM_FIELDS as FORM_FIELDS,
  VALIDATION_ERROR_MESSAGES
} from './AddVoucherThemesConstants';


// Validation rules
const getValidationRules = (state) => [
  {
    check: () => state.themeName.trim().length > 0,
    error: VALIDATION_ERROR_MESSAGES.themeName,
  },
  {
    check: () => state.emailSubject.trim().length > 0,
    error: VALIDATION_ERROR_MESSAGES.emailSubject,
  },
  {
    check: () => state.frontImageUrl.trim().length > 0,
    error: VALIDATION_ERROR_MESSAGES.frontImageUrl,
  },
  {
    check: () => state.backImageUrl.trim().length > 0,
    error: VALIDATION_ERROR_MESSAGES.backImageUrl,
  },
  {
    check: () => state.mobileImageUrl.trim().length > 0,
    error: VALIDATION_ERROR_MESSAGES.mobileImageUrl,
  },
  {
    check: () => state.smsMessage.trim().length > 0,
    error: VALIDATION_ERROR_MESSAGES.smsMessage,
  },
  {
    check: () => state.emailMessage.trim().length > 0,
    error: VALIDATION_ERROR_MESSAGES.emailMessage,
  },
  {
    check: () => state.selectedCategoryId.trim().length > 0,
    error: 'Category selection is required',
  },
];

// Custom hooks
const useVoucherThemeCategories = (token, setState) => {
  return useCallback(async () => {
    setState({ loading: true, error: '' });
    try {
      const response = await getVoucherThemeCategories(token);
      if (response.voucherThemeCategories) {
        setState({ loading: false, categories: response.voucherThemeCategories });
      }
    } catch (error) {
      setState({ error: TOAST_MESSAGES.categoryError, loading: false });
    }
  }, [token]);
};

const useFormValidation = (state, setState) => {
  return useCallback(() => {
    const rules = getValidationRules(state);
    const failed = rules.find((rule) => !rule.check());

    if (failed) {
      setState((prev) => ({
        ...prev,
        error: failed.error,
        validated: true,
      }));
      return false;
    }

    setState((prev) => ({
      ...prev,
      error: null,
      validated: true,
    }));

    return true;
  }, [state, setState]);
};

// Utility functions
const createPayload = (state) => ({
  themeName: state.themeName,
  smsContent: state.smsMessage,
  emailContent: state.emailMessage,
  emailSubject: state.emailSubject,
  backgroundImageUrl: state.backgroundImageUrl,
  thumbnailImageUrl: state.thumbnailImageUrl,
  imageFrontUrl: state.frontImageUrl,
  imageBackUrl: state.backImageUrl,
  mobileImageUrl: state.mobileImageUrl,
});

const resetFormState = (setState) => {
  setState({
    ...INITIAL_STATE,
    categories: [], // Preserve categories
  });
};

// Components
const FormField = ({ field, value, onChange, validated }) => {
  const { name, label, type, placeholder, required, validationMessage, helpText, rows } = field;

  return (
    <Form.Group as={Row} className='mb-3'>
      <Form.Label column sm={3}>
        {label} {required && <span className='text-danger'>*</span>}
      </Form.Label>
      <Col sm={9}>
        <Form.Control
          as={type === 'textarea' ? 'textarea' : 'input'}
          type={type !== 'textarea' ? type : undefined}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          required={required}
        />
        <Form.Control.Feedback type='invalid'>
          {validationMessage}
        </Form.Control.Feedback>
        {helpText && (
          <Form.Text className='text-muted'>
            {helpText}
          </Form.Text>
        )}
      </Col>
    </Form.Group>
  );
};

const CategorySelect = ({ categories, value, onChange, validated }) => (
  <Form.Group as={Row} className='mb-3'>
    <Form.Label column sm={3}>
      Voucher Theme Category <span className='text-danger'>*</span>
    </Form.Label>
    <Col sm={9}>
      <Form.Control
        as="select"
        name="selectedCategoryId"
        className='form-select'
        value={value}
        onChange={onChange}
        required
        isInvalid={validated && !value}
      >
        <option value=''>Select a category</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        ))}
      </Form.Control>

      <Form.Control.Feedback type='invalid'>
        Please select a category.
      </Form.Control.Feedback>
    </Col>
  </Form.Group>
);


const FormActions = ({ onCancel, loading }) => (
  <div className={styles.formActions}>
    <Button variant='secondary' className='me-2' onClick={onCancel}>
      Cancel
    </Button>
    <Button type='submit' variant='primary' disabled={loading}>
      {loading ? 'Adding...' : 'Add Theme'}
    </Button>
  </div>
);

// Main component
const AddVoucherThemes = ({ auth }) => {
  const [state, setState] = useComponentState(INITIAL_STATE);

  const fetchCategories = useVoucherThemeCategories(auth.auth.token, setState);
  const validateForm = useFormValidation(state, setState);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setState({ [name]: value, error: '' });
  }, [setState]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setState({ validated: true });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setState({ loading: true, error: '' });

    try {
      const payload = createPayload(state);
      const response = await AddVoucherThemesApi(payload, auth.auth.token);

      if (response.message === SUCCESS_MESSAGE) {
        toast.success(TOAST_MESSAGES.success);
        resetFormState(setState);
        setState((prev) => ({ ...prev, categories: state.categories })); // Preserve categories
      } else {
        toast.error(TOAST_MESSAGES.error);
        setState({ loading: false });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || TOAST_MESSAGES.error;
      setState({
        loading: false,
        error: errorMessage,
      });
      toast.error(errorMessage);
    }
  }, [state, validateForm, setState, auth.auth.token]);

  const handleCancel = useCallback(() => {
    resetFormState(setState);
    setState((prev) => ({ ...prev, categories: state.categories })); // Preserve categories
  }, [setState, state.categories]);

  const formFields = useMemo(() =>
    FORM_FIELDS.map(field => (
      <FormField
        key={field.name}
        field={field}
        value={state[field.name]}
        onChange={handleInputChange}
        validated={state.validated}
      />
    )), [state, handleInputChange]
  );

  return (
    <div className={styles.root_div}>
      <PageHeader text='Add Voucher Theme' />

      <Card className={styles.formCard}>
        <Card.Body>
          {state.error && <Alert variant='danger'>{state.error}</Alert>}
          {state.success && (
            <Alert variant='success'>{TOAST_MESSAGES.success}</Alert>
          )}

          <Form noValidate validated={state.validated} onSubmit={handleSubmit}>
            <div className='mb-3'>
              <small className='text-muted'>
                Fields marked with <span className='text-danger'>*</span> are required
              </small>
            </div>

            {/* Theme Name Field */}
            {formFields[0]}

            {/* Image URL Fields */}
            {formFields.slice(1, 6)}

            {/* Email and SMS Fields */}
            {formFields.slice(6)}

            <FormActions onCancel={handleCancel} loading={state.loading} />
          </Form>

          {state.loading && <Loading />}
        </Card.Body>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AddVoucherThemes);